import type { CreateSongData, PerformingArtist, UpdateSongData, WriterSplit } from "../hooks/types";

export function formatWorkForApi(song: CreateSongData, selectedAccount: string) {
  const {
    title,
    durationmins,
    durationsecs,
    isremix,
    containssamples,
    ispublicdomain,
    alternativetitles = [],
    performingartistnames = [],
    writersplits = [],
    recordings = [],
  } = song;

  const filteredAlts = alternativetitles.filter(a => Boolean(a.title) && a.title.length > 0);

  return {
    artistaccountcode: selectedAccount,
    title,
    durationmins,
    durationsecs,
    isremix,
    containssamples,
    ispublicdomain: ispublicdomain ?? false,
    alternativetitles: filteredAlts,
    performingartistnames,
    writersplits: writersplits.map(w => ({
      writercode: w.writercode,
      writershare: w.writershare,
      writerdesignationcode: w.writerdesignationcode,
      righttocollect: Boolean(w.righttocollect),
    })),
    ...(recordings.length > 0 && { recordings }),
  };
}

export function formatUpdateWorkForApi(songData: UpdateSongData, selectedAccount: string) {
  const {
    title,
    durationmins,
    durationsecs,
    isremix,
    containssamples,
    alternativetitles = [],
    performingartistnames = [],
    writersplits = [],
    workcode,
    etag,
    iswc,
    recordings,
  } = songData;

  const filteredAlts = alternativetitles.filter(a => Boolean(a.title) && a.title.length > 0);
  const performersFormatted = formatPerformersForApi(performingartistnames);
  const writersplitsFormatted = formatWriterSplitsForApi(writersplits);

  return {
    artistaccountcode: selectedAccount,
    title,
    durationmins,
    durationsecs,
    isremix,
    containssamples,
    ispublicdomain: true,
    alternativetitles: filteredAlts,
    performingartistnames: performersFormatted,
    writersplits: writersplitsFormatted,
    workcode,
    etag,
    iswc,
    recordings,
  };
}

function formatPerformersForApi(performingartistnames: PerformingArtist[]) {
  return performingartistnames.map(a => ({
    performingartistname: a.name,
    performingartistidentifier: Object.prototype.hasOwnProperty.call(a, "performingartistidentifier")
      ? a.performingartistidentifier
      : "00000000-0000-0000-0000-000000000000",
    performingartistamendmentidentifier: Object.prototype.hasOwnProperty.call(a, "performingartistamendmentidentifier")
      ? a.performingartistamendmentidentifier
      : "00000000-0000-0000-0000-000000000000",
  }));
}

const DESIGNATION_MAP: Record<number, string> = {
  1: "Author",
  2: "Composer",
  3: "ComposerAuthor",
};

function formatWriterSplitsForApi(writersplits: WriterSplit[]) {
  return writersplits.map((a) => {
    const designationCode = typeof a.writerdesignationcode === "number"
      ? DESIGNATION_MAP[a.writerdesignationcode] || "ComposerAuthor"
      : a.writerdesignationcode;

    return {
      writercode: a.writercode,
      writershare: a.writershare,
      writerdesignationcode: designationCode,
      righttocollect: a.righttocollect,
      writersplitamendmentidentifier: Object.prototype.hasOwnProperty.call(a, "writersplitamendmentidentifier")
        ? a.writersplitamendmentidentifier
        : "00000000-0000-0000-0000-000000000000",
      writersplitidentifier: Object.prototype.hasOwnProperty.call(a, "writersplitidentifier")
        ? a.writersplitidentifier
        : "00000000-0000-0000-0000-000000000000",
      ...(a.firstname !== undefined && { firstname: a.firstname }),
      ...(a.lastname !== undefined && { lastname: a.lastname }),
      ...(a.middlename !== undefined && { middlename: a.middlename }),
      ...(a.writersociety !== undefined && { writersociety: a.writersociety }),
      ...(a.dateofbirth !== undefined && { dateofbirth: a.dateofbirth }),
    };
  });
}
